import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Menu, X, User, LogOut } from "lucide-react";
import Logo from "./Logo";
import AccountMenu from "./AccountMenu";
import { useAuth } from "../auth/AuthContext";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/offers", label: "Offers" },
    { to: "/coupons", label: "Coupons" },
    { to: "/brands", label: "Brands" },
    { to: "/categories", label: "Categories" },
    { to: "/blog", label: "Blog" },
    { to: "/price-comparison", label: "Compare" },
    { to: "/flights", label: "Flights" },
    { to: "/hotels", label: "Hotels" },
    { to: "/card-offers", label: "Card Offers" },
];

const SECONDARY = [
    { to: "/about", label: "About" },
    { to: "/features", label: "Features" },
    { to: "/contact", label: "Contact" },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { user, logout } = useAuth();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const linkCls = (isActive) =>
        `relative text-sm font-medium transition-colors hover:text-saffron ${isActive ? "text-saffron" : "text-ink"}`;

    return (
        <>
            <header
                data-testid="site-navbar"
                className="fixed top-0 z-50 w-full border-b border-ink/5 bg-cream/80 backdrop-blur-xl"
            >
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
                    <Logo />
                    <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
                        {LINKS.map(({ to, label }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/"}
                                data-testid={`nav-link-${label.toLowerCase()}`}
                                className={({ isActive }) => linkCls(isActive)}
                            >
                                {label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2.5">
                        <Link
                            to="/search"
                            data-testid="nav-search-link"
                            aria-label="AI Search"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 text-ink transition-colors hover:border-saffron hover:text-saffron"
                        >
                            <Search className="h-5 w-5" />
                        </Link>
                        {user ? (
                            <div className="hidden sm:block">
                                <AccountMenu />
                            </div>
                        ) : (
                            <div className="hidden items-center gap-2.5 sm:flex">
                                <Link to="/login" data-testid="nav-login-link" className="px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:text-saffron">
                                    Login
                                </Link>
                                <Link
                                    to="/signup"
                                    data-testid="nav-signup-button"
                                    className="rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-india"
                                >
                                    Sign up
                                </Link>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="nav-mobile-menu-button"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 text-ink xl:hidden"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-ink px-6 pb-10 pt-28 xl:hidden"
                    >
                        <nav className="flex flex-col gap-1" aria-label="Mobile">
                            {LINKS.map(({ to, label }, i) => (
                                <motion.div
                                    key={to}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 + i * 0.05, duration: 0.4 }}
                                >
                                    <Link
                                        to={to}
                                        data-testid={`nav-mobile-link-${label.toLowerCase()}`}
                                        className="block py-2.5 font-display text-3xl font-semibold tracking-tight text-cream transition-colors hover:text-saffron"
                                    >
                                        {label}
                                    </Link>
                                </motion.div>
                            ))}
                            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36, duration: 0.4 }}>
                                <Link
                                    to="/search"
                                    data-testid="nav-mobile-link-search"
                                    className="block py-2.5 font-display text-3xl font-semibold tracking-tight text-saffron"
                                >
                                    AI Search
                                </Link>
                            </motion.div>
                        </nav>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.45, duration: 0.4 }}
                            className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t border-cream/10 pt-6"
                        >
                            {SECONDARY.map(({ to, label }) => (
                                <Link
                                    key={to}
                                    to={to}
                                    data-testid={`nav-mobile-link-${label.toLowerCase()}`}
                                    className="text-sm font-medium text-cream/70 transition-colors hover:text-saffron"
                                >
                                    {label}
                                </Link>
                            ))}
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.4 }}
                            className="mt-auto pt-10"
                        >
                            {user ? (
                                <div className="flex flex-col gap-3">
                                    <Link
                                        to="/profile"
                                        data-testid="nav-mobile-profile-link"
                                        className="inline-flex w-full items-center justify-center rounded-full bg-saffron px-8 py-4 font-medium text-white"
                                    >
                                        My account
                                    </Link>
                                    <div className="flex gap-3">
                                        <Link to="/saved" data-testid="nav-mobile-saved-link" className="flex-1 rounded-full border border-cream/25 px-4 py-3.5 text-center text-sm font-medium text-cream">
                                            Saved
                                        </Link>
                                        <Link to="/notifications" data-testid="nav-mobile-notifications-link" className="flex-1 rounded-full border border-cream/25 px-4 py-3.5 text-center text-sm font-medium text-cream">
                                            Alerts
                                        </Link>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={logout}
                                        data-testid="nav-mobile-logout-button"
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-cream/25 px-8 py-4 font-medium text-cream"
                                    >
                                        <LogOut className="h-5 w-5" />
                                        Log out ({user.name?.split(" ")[0]})
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <Link
                                        to="/signup"
                                        data-testid="nav-mobile-signup-button"
                                        className="inline-flex w-full items-center justify-center rounded-full bg-saffron px-8 py-4 font-medium text-white"
                                    >
                                        Sign up free
                                    </Link>
                                    <Link
                                        to="/login"
                                        data-testid="nav-mobile-login-link"
                                        className="inline-flex w-full items-center justify-center rounded-full border border-cream/25 px-8 py-4 font-medium text-cream"
                                    >
                                        Login
                                    </Link>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
